import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  DialogProps,
} from '@mui/material';
import { Close } from '@mui/icons-material';

export interface MuiDialogProps extends Omit<DialogProps, 'title'> {
  title?: React.ReactNode;
  onClose?: () => void;
  actions?: React.ReactNode;
  showCloseButton?: boolean;
  description?: React.ReactNode;
}

/**
 * Native MUI Dialog component with consistent styling and behavior
 * Replaces shadcn Dialog components with full MUI implementation
 */
export const MuiDialog: React.FC<MuiDialogProps> = ({
  title,
  children,
  onClose,
  actions,
  showCloseButton = true,
  description,
  ...dialogProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      {...dialogProps}
      onClose={onClose}
      fullScreen={isMobile && dialogProps.fullWidth}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          minWidth: isMobile ? '100%' : 400,
          ...dialogProps.PaperProps?.sx,
        },
        ...dialogProps.PaperProps,
      }}
    >
      {title && (
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: showCloseButton ? 1 : 3,
          }}
        >
          <Box>
            <Typography variant="h6" component="div">
              {title}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {description}
              </Typography>
            )}
          </Box>
          {showCloseButton && onClose && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                color: 'grey.500',
              }}
            >
              <Close />
            </IconButton>
          )}
        </DialogTitle>
      )}
      
      <DialogContent
        sx={{
          pb: actions ? 2 : 3,
        }}
      >
        {children}
      </DialogContent>
      
      {actions && (
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};


